import React, { useState } from 'react';
import Header from './Header.jsx';
import Contact from './Contact.jsx';
import AppHead from './AppHead.jsx';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Footer from './Footer.jsx';
import { 
    Search, HelpCircle, User, Ticket, CreditCard, 
    RotateCcw, Headphones, BookOpen, MessageSquare,
    Phone, Mail, Clock, Star, TrendingUp, Filter,
    ChevronRight, Lightbulb, Shield, Globe
} from 'lucide-react';

const HelpCard = ({ icon: Icon, title, description, slug, category }) => {
    return (
        <motion.div
            whileHover={{ y: -8, scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
        >
            <Link 
                to={`/help-center/${slug}`}
                className="block bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 group overflow-hidden relative"
            >
                {/* Background Pattern */}
                <div className="absolute inset-0 bg-gradient-to-br from-orange-50/30 to-teal-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Content */}
                <div className="relative z-10">
                    <div className="flex items-start gap-5">
                        <div className="flex-shrink-0">
                            <div className="w-14 h-14 bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 rounded-xl flex items-center justify-center text-white! group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-xl">
                                <Icon className="w-7 h-7" />
                            </div>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-3">
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-orange-100 to-orange-200 text-orange-800 border border-orange-200">
                                    {category}
                                </span>
                            </div>
                            <h3 className="text-lg font-bold text-gray-900! mb-3 group-hover:text-orange-600 transition-colors duration-300 leading-tight">
                                {title}
                            </h3>
                            <p className="text-gray-600! text-sm leading-relaxed mb-4">
                                {description}
                            </p>
                            <div className="flex items-center text-orange-500 text-sm font-semibold group-hover:text-orange-600 transition-colors duration-300">
                                Learn more
                                <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                            </div>
                        </div>
      </div>
      </div>
                
                {/* Hover Effect Border */}
                <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-orange-200 transition-all duration-300"></div>
    </Link>
        </motion.div>
    );
};

const HelpCenter = () => {
    const [search, setSearch] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');

  const helpTopics = [
    {
      id: 1,
            icon: User,
      title: "Managing Your Account",
      slug: "managing-your-account",
            category: "Account",
            description: "Learn how to update your profile, change passwords, and manage your account settings."
    },
    {
      id: 2,
            icon: Ticket,
      title: "Booking Tickets",
      slug: "booking-tickets",
            category: "Tickets",
            description: "Step-by-step guide to purchasing tickets for events on EventPlots platform."
    },
    {
      id: 3,
            icon: CreditCard,
      title: "Payment Options",
      slug: "payment-options",
            category: "Payments",
            description: "Explore available payment methods including mobile money, cards, and bank transfers."
    },
    {
      id: 4,
            icon: RotateCcw,
      title: "Refunds and Cancellations",
      slug: "refunds-and-cancellations",
            category: "Refunds",
            description: "Understand our refund policy and how to cancel bookings when needed."
    },
    {
      id: 5,
            icon: Headphones,
      title: "Customer Support",
      slug: "customer-support",
            category: "Support",
            description: "Get help from our support team for any issues or questions you may have."
        },
        {
            id: 6,
            icon: BookOpen,
            title: "Creating Events",
            slug: "creating-events",
            category: "Events",
            description: "Learn how to create and manage your own events on the platform."
        },
        {
            id: 7,
            icon: Shield,
            title: "Security & Privacy",
            slug: "security-privacy",
            category: "Security",
            description: "Information about data protection, security measures, and privacy policies."
        },
        {
            id: 8,
            icon: Globe,
            title: "Platform Features",
            slug: "platform-features",
            category: "Features",
            description: "Discover all the features and tools available on EventPlots."
        }
    ];

    const categories = [
        { id: 'all', name: 'All Topics', count: helpTopics.length },
        { id: 'account', name: 'Account', count: helpTopics.filter(t => t.category.toLowerCase() === 'account').length },
        { id: 'tickets', name: 'Tickets', count: helpTopics.filter(t => t.category.toLowerCase() === 'tickets').length },
        { id: 'payments', name: 'Payments', count: helpTopics.filter(t => t.category.toLowerCase() === 'payments').length },
        { id: 'refunds', name: 'Refunds', count: helpTopics.filter(t => t.category.toLowerCase() === 'refunds').length },
        { id: 'support', name: 'Support', count: helpTopics.filter(t => t.category.toLowerCase() === 'support').length },
        { id: 'events', name: 'Events', count: helpTopics.filter(t => t.category.toLowerCase() === 'events').length },
        { id: 'security', name: 'Security', count: helpTopics.filter(t => t.category.toLowerCase() === 'security').length },
        { id: 'features', name: 'Features', count: helpTopics.filter(t => t.category.toLowerCase() === 'features').length }
    ];

    const filteredHelps = helpTopics.filter(item => {
        const matchesSearch = item.title.toLowerCase().includes(search.toLowerCase()) ||
                            item.description.toLowerCase().includes(search.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || 
                              item.category.toLowerCase() === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    const popularArticles = [
        "How to reset your password",
        "Understanding ticket refunds",
        "Setting up mobile money payments",
        "Creating your first event",
        "Managing event notifications"
    ];

  return (
        <div className="min-h-screen bg-gray-50">
      <Header />
      <AppHead
        title="Help Center"
                description="Get help and support for EventPlots platform"
        imageUrl="http://localhost:3000/images/logo.png"
        url={window.location.hostname}
        twitterCardType="summary_large_image"
      />

            {/* Hero Section */}
            <section className="bg-gradient-to-br from-orange-50 via-white to-teal-50 py-16">
                <div className="container mx-auto px-4 max-w-6xl">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center mb-12"
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-medium mb-6">
                            <HelpCircle className="w-4 h-4" />
                            Help Center
                        </div>
                        <h1 className="text-lg font-bold text-gray-900 mb-6">
                            How can we help you?
                        </h1>
                        <p className="text-sm text-gray-600 max-w-2xl mx-auto mb-8">
                            Find answers to common questions, learn how to use EventPlots, and get support when you need it.
                        </p>
                        
                        {/* Search Bar */}
                        <div className="max-w-2xl mx-auto">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
                                    type="text"
                                    placeholder="Search for help articles..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
                                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm shadow-sm"
                                />
                            </div>
                        </div>
                    </motion.div>

                    {/* Popular Articles */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="bg-white rounded-xl p-6 shadow-lg border border-gray-100"
                    >
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-orange-500" />
                            Popular Articles
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {popularArticles.map((article, index) => (
                                <div key={index} className="flex items-center gap-2 text-sm text-gray-600 hover:text-orange-600 transition-colors duration-200 cursor-pointer">
                                    <Star className="w-4 h-4 text-orange-400" />
                                    {article}
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Categories Filter */}
            <section className="py-8 bg-white border-b border-gray-200">
                <div className="container mx-auto px-4 max-w-6xl">
                    <div className="flex flex-wrap gap-2">
                        {categories.map((category) => (
                            <button
                                key={category.id}
                                onClick={() => setSelectedCategory(category.id)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                    selectedCategory === category.id
                                        ? 'bg-orange-500 text-white shadow-md'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                {category.name}
                                <span className="ml-2 text-xs opacity-75">({category.count})</span>
                            </button>
                        ))}
          </div>
      </div>
            </section>

            {/* Help Topics Grid */}
            <section className="py-16">
                <div className="container mx-auto px-4 max-w-6xl">
                    <AnimatePresence mode="wait">
                        {filteredHelps.length > 0 ? (
                            <motion.div
                                key={`${search}-${selectedCategory}`}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                            >
                                {filteredHelps.map((item) => (
                                    <HelpCard
              key={item.id} 
                                        icon={item.icon}
              title={item.title} 
                                        description={item.description}
              slug={item.slug}
                                        category={item.category}
                                    />
                                ))}
                            </motion.div>
                        ) : (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="text-center py-12"
                            >
                                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Search className="w-12 h-12 text-gray-400" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">No results found</h3>
                                <p className="text-sm text-gray-600 mb-6">
                                    Try adjusting your search terms or browse all categories.
                                </p>
                                <button
                                    onClick={() => {
                                        setSearch('');
                                        setSelectedCategory('all');
                                    }}
                                    className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors duration-200"
                                >
                                    Clear filters
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </section>

            {/* Quick Contact Section */}
            <section className="py-20 bg-gradient-to-br from-orange-400 via-orange-500 to-teal-500 relative overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="absolute top-0 left-0 w-full h-full">
                    <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
                    <div className="absolute bottom-10 right-10 w-40 h-40 bg-white/10 rounded-full blur-xl"></div>
                    <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-white/5 rounded-full blur-lg"></div>
                </div>
                
                <div className="container mx-auto px-4 max-w-5xl text-center relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium text-white mb-6">
                            <MessageSquare className="w-4 h-4" />
                            Need Assistance?
                        </div>
                        <h2 className="text-lg font-bold text-white mb-6">
                            Still need help?
                        </h2>
                        <p className="text-sm text-orange-100 mb-12 max-w-3xl mx-auto leading-relaxed">
                            Our dedicated support team is here to help you with any questions, issues, or guidance you might need.
                        </p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                viewport={{ once: true }}
                                className="group"
                            >
                                <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 border border-white/30 hover:bg-white/30 transition-all duration-300 hover:scale-105">
                                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4 mx-auto group-hover:bg-white/30 transition-all duration-300">
                                        <Phone className="w-6 h-6 text-white" />
                                    </div>
                                    <div className="text-center">
                                        <div className="text-sm text-orange-100 mb-1">Call us</div>
                                        <div className="font-bold text-white text-sm">+256 700 000 000</div>
                                        <div className="text-xs text-orange-100 mt-1">Mon-Fri: 8AM-6PM</div>
                                    </div>
                                </div>
                            </motion.div>
                            
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                viewport={{ once: true }}
                                className="group"
                            >
                                <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 border border-white/30 hover:bg-white/30 transition-all duration-300 hover:scale-105">
                                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4 mx-auto group-hover:bg-white/30 transition-all duration-300">
                                        <Mail className="w-6 h-6 text-white" />
                                    </div>
                                    <div className="text-center">
                                        <div className="text-sm text-orange-100 mb-1">Email us</div>
                                        <div className="font-bold text-white text-sm">support@eventplots.com</div>
                                        <div className="text-xs text-orange-100 mt-1">24/7 Response</div>
      </div>
    </div>
                            </motion.div>
                            
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.3 }}
                                viewport={{ once: true }}
                                className="group"
                            >
                                <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 border border-white/30 hover:bg-white/30 transition-all duration-300 hover:scale-105">
                                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4 mx-auto group-hover:bg-white/30 transition-all duration-300">
                                        <Clock className="w-6 h-6 text-white" />
      </div>
                                    <div className="text-center">
                                        <div className="text-sm text-orange-100 mb-1">Live Chat</div>
                                        <div className="font-bold text-white text-sm">Available Now</div>
                                        <div className="text-xs text-orange-100 mt-1">Instant Support</div>
      </div>
    </div>
                            </motion.div>
                        </div>
                        
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                            viewport={{ once: true }}
                            className="mt-10"
                        >
                            <button className="px-8 py-4 bg-white text-orange-600 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 hover:shadow-lg hover:scale-105">
                                Start Live Chat
                            </button>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            <Contact />
            <Footer />
  </div>
    );
};

export default HelpCenter;